import { useState } from "react";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { useLanguage } from "@/context/LanguageContext";
import { auctionsLabels } from "@/i18n";
import { useFile } from "@/hooks/useFile";
import { useMedia } from "@/hooks/useMedia";
import { useUser } from "@/hooks/useUser";
import { SellerContractService } from "@/services/sellerContract/sellerContractService";
import { ListingMyListings } from "@/types/listing";

interface SellerContractPopupProps {
  listing: ListingMyListings | null;
  onClose: () => void;
}

type Labels = Record<string, string>;

const formatDateTime = (value: string | null | undefined, fallback: string) =>
  value ? new Date(value).toLocaleString("sv-SE") : fallback;

export const SellerContractPopup = ({
  listing,
  onClose,
}: SellerContractPopupProps) => {
  const { language } = useLanguage();
  const sc = (auctionsLabels(language).myAuctions.sellerContract ??
    {}) as Labels;
  const { downloadFile } = useFile();
  const { getDownloadUrlAsync } = useMedia();
  const { userDetails } = useUser();
  const navigate = useNavigate();
  const [isStartingSignature, setIsStartingSignature] = useState(false);

  const contract = listing?.sellerContract;
  if (!listing || !contract) return null;

  const status = contract.signatureStatus;
  const isSigned = status === "signed";
  const isDeclined = status === "declined";
  const hasDocument = Boolean(contract.document);
  // Only offer signing once admin has requested it (matches the overview badge).
  const canSign = status === "requested" && hasDocument;
  const isBankIdApproved = userDetails?.bankIdStatus === "approved";
  const na = sc.notAvailable ?? "-";

  const statusLabel = isSigned
    ? sc.signatureSigned
    : isDeclined
      ? sc.signatureDeclined
      : status === "requested"
        ? sc.signatureRequested
        : sc.signatureNotRequested;

  const badgeClass = isSigned
    ? "bg-green-100 text-green-800"
    : isDeclined
      ? "bg-red-100 text-red-800"
      : status === "requested"
        ? "bg-amber-100 text-amber-800"
        : "bg-gray-100 text-gray-800";

  const methodLabel =
    contract.signatureMethod === "bankid_idura"
      ? sc.methodBankId
      : contract.signatureMethod === "physical_id_check"
        ? sc.methodPhysical
        : contract.signatureMethod === "signed_externally"
          ? sc.methodExternal
          : contract.signatureMethod === "no_signature_required"
            ? sc.methodNotRequired
            : contract.signatureMethod === "other"
              ? sc.methodOther
              : sc.methodUnknown;

  const handleDownload = async () => {
    if (!contract.document) return;
    try {
      const url = await getDownloadUrlAsync({
        mediaId: contract.document.mediaId,
        expiresIn: 3600,
      });
      const filename = await downloadFile(url, contract.document.name);
      toast.success(sc.downloadStarted, {
        description: (sc.downloadDescription ?? "{filename}").replace(
          "{filename}",
          filename,
        ),
      });
    } catch (error) {
      toast.error(sc.downloadError, {
        description: error instanceof Error ? error.message : undefined,
      });
    }
  };

  const handleStartBankIdSignature = () => {
    setIsStartingSignature(true);
    void (async () => {
      try {
        const { isNativeCapacitorApp } = await import(
          "@/integrations/clients/carAuction/nativeAuth"
        );
        if (isNativeCapacitorApp()) {
          const continueUrl =
            await SellerContractService.getNativeBankIdSignatureContinueUrl(
              contract.id,
            );
          const { openBankIdInSystemBrowser } = await import(
            "@/lib/native/bankIdBrowser"
          );
          await openBankIdInSystemBrowser(continueUrl);
          // Browser owns the flow; clear overlay so the dialog isn't stuck.
          setIsStartingSignature(false);
          onClose();
          return;
        }
        window.location.href = SellerContractService.getBankIdSignatureStartUrl(
          contract.id,
        );
      } catch {
        setIsStartingSignature(false);
        toast.error(sc.startSignatureError);
      }
    })();
  };

  const vehicle = listing.vehicleDetails;

  return (
    <Dialog open={!!listing} onOpenChange={onClose}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>{sc.title}</DialogTitle>
        </DialogHeader>
        <ScrollArea className="flex-1 max-h-[60vh] pr-4">
          <div className="space-y-5 p-1">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium text-primary/60">
                  {sc.contractNumber}
                </Label>
                <p className="mt-1 text-sm">{contract.contractNumber || na}</p>
              </div>
              <div>
                <Label className="text-sm font-medium text-primary/60">
                  {sc.signatureStatus}
                </Label>
                <div className="mt-2">
                  <Badge className={badgeClass}>{statusLabel}</Badge>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium text-primary/60">
                  {sc.signatureMethod}
                </Label>
                <p className="mt-1 text-sm">{methodLabel}</p>
              </div>
              <div>
                <Label className="text-sm font-medium text-primary/60">
                  {isSigned ? sc.signatureDate : sc.signatureRequestedDate}
                </Label>
                <p className="mt-1 text-sm">
                  {isSigned
                    ? formatDateTime(contract.signedAt, na)
                    : formatDateTime(contract.signatureRequestedAt, na)}
                </p>
              </div>
            </div>

            {contract.signedByName && (
              <div>
                <Label className="text-sm font-medium text-primary/60">
                  {sc.signedBy}
                </Label>
                <p className="mt-1 text-sm">{contract.signedByName}</p>
              </div>
            )}

            {contract.signatureNote && (
              <div>
                <Label className="text-sm font-medium text-primary/60">
                  {sc.note}
                </Label>
                <p className="mt-1 text-sm">{contract.signatureNote}</p>
              </div>
            )}

            <div className="border rounded p-3 bg-muted">
              <div className="font-semibold mb-1">{sc.vehicle}</div>
              <div className="text-sm">{listing.title}</div>
              {vehicle && (
                <div className="text-sm text-muted-foreground">
                  {[vehicle.regNumber, vehicle.make, vehicle.model, vehicle.year]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
              )}
            </div>

            <div className="border-t pt-4">
              <h3 className="text-sm font-medium text-primary/60 mb-3">
                {sc.documents}
              </h3>
              {contract.document ? (
                <div className="flex items-center justify-between gap-3 p-2 rounded-lg border">
                  <span className="text-sm min-w-0 flex-1 break-words">
                    {sc.contractFile} ({contract.document.name})
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleDownload}
                    aria-label={sc.download}
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  {sc.noDocument}
                </p>
              )}
            </div>
          </div>
        </ScrollArea>

        <div className="flex flex-col items-end gap-2 pt-4 border-t">
          {canSign && !isBankIdApproved && (
            <p className="w-full text-sm text-muted-foreground text-left">
              {sc.verifyBankIdHint ?? sc.signWithoutBankIdHint}
            </p>
          )}
          <div className="flex justify-end gap-2 w-full">
            {canSign && !isBankIdApproved && (
              <Button
                className="gap-2"
                onClick={() => {
                  onClose();
                  navigate("/settings?tab=profile");
                }}
              >
                <img
                  src="/assets/BankID_logo.png"
                  alt="BankID"
                  className="h-4 w-auto object-contain"
                />
                {sc.verifyBankIdCta ?? "Verifiera BankID"}
              </Button>
            )}
            {canSign && isBankIdApproved && (
                <Button
                  className="gap-2"
                  onClick={handleStartBankIdSignature}
                  disabled={isStartingSignature}
                >
                  <img
                    src="/assets/BankID_logo.png"
                    alt="BankID"
                    className="h-4 w-auto object-contain"
                  />
                  {sc.signWithBankId}
                </Button>
              )}
            <Button variant="outline" onClick={onClose}>
              {sc.close}
            </Button>
          </div>
        </div>

        {isStartingSignature && (
          <div className="absolute inset-0 z-[130] bg-background/90 backdrop-blur-sm flex items-center justify-center">
            <div className="flex flex-col items-center gap-4 rounded-2xl border bg-background px-8 py-7 shadow-xl">
              <Loader2 className="h-10 w-10 animate-spin text-primary" />
              <div className="text-center">
                <div className="text-base font-semibold">
                  {sc.redirectingToBankId}
                </div>
                <div className="text-sm text-muted-foreground mt-1">
                  {sc.redirectingToBankIdDescription}
                </div>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SellerContractPopup;
